import { Button, IconButton, makeStyles } from "@material-ui/core";
import { ChevronLeft, ChevronRight } from "@material-ui/icons";
import { Colors } from "styles/Colors";
import Store from "common/store";
import StoreKeys from "common/storeKeys";
import * as React from "react";
import EventFormatService from "services/EventFormatService";
import EventFormatBracketBuilder from "./EventFormatBracketBuilder";
import EventFormatDetails from "./EventFormatDetails";
import EventFormatHelper, {
	buildScoringDetails,
	championPickCoordinatesResolve,
} from "common/helpers/eventFormatHelper";
import { SetLoading } from "common/components/Loader/Loader";
import { toast } from "react-toastify";
// eslint-disable-next-line no-unused-vars
import { EventFormatBuilder } from "common/helpers/eventFormatHelper";
import { EventStageTypes } from "common/constants/EventStageTypes";

const useStyles = makeStyles(() => {
	return {
		root: {
			height: "100%",
			minHeight: 0,
			display: "flex",
			flexDirection: "row",
		},
		sidebar: {
			flex: "0 0 340px",
			minHeight: 0,
			display: "flex",
			flexDirection: "column",
			paddingRight: 12,
			borderRight: `1px solid ${Colors.panel}`,
		},
		sidebarHeader: {
			flex: "0 0 auto",
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
		},
		sidebarTitle: {
			margin: 0,
			fontSize: "1.1rem",
			color: Colors.primary,
		},
		sidebarBody: {
			flex: 1,
			minHeight: 0,
			overflowY: "auto",
			paddingTop: 12,
		},
		sidebarFooter: {
			flex: "0 0 auto",
			display: "flex",
			flexDirection: "column",
			gap: 8,
			marginTop: 12,
			paddingTop: 12,
			borderTop: `1px solid ${Colors.panel}`,
		},
		collapseRail: {
			flex: "0 0 auto",
			display: "flex",
			alignItems: "flex-start",
			paddingRight: 4,
			borderRight: `1px solid ${Colors.panel}`,
		},
		main: {
			flex: 1,
			minWidth: 0,
			minHeight: 0,
			display: "flex",
			paddingLeft: 12,
		},
	};
});

export default function EventFormatEditor() {
	const classNames = useStyles();
	const [sidebarOpen, setSidebarOpen] = React.useState(true);

	const validateFormat = (eventFormat, data) => {
		if (!eventFormat.name) {
			toast.error("Event format needs a name.");
			return false;
		}

		const championPick = data?.championPick;
		if (championPick?.enabled) {
			if (!(Number(championPick.points) > 0)) {
				toast.error("Going All The Way needs points greater than 0.");
				return false;
			}

			if (!championPickCoordinatesResolve(data)) {
				toast.error(
					"Going All The Way's final match doesn't exist - re-pick its stage, part and column."
				);
				return false;
			}

			if (
				(championPick.teamStageIndexes ?? []).some((i) => !data.stages[i])
			) {
				toast.error("Re-pick Going All The Way's eligible team stages.");
				return false;
			}

			const nameClash = data.stages.some((s) =>
				s?.bracket?.scoringGroups?.some(
					(g) => g?.name === "Going All The Way"
				)
			);
			if (nameClash) {
				toast.error(
					'A bracket scoring group can\'t be named "Going All The Way" - that name is reserved for the champion bonus.'
				);
				return false;
			}
		}

		return true;
	}

	const save = async () => {
		/** @type {EventFormatBuilder} */
		const formatBuilder = Store.get(
			StoreKeys.EVENTFORMATBUILDER.CURRENT_FORMAT
		);

		// Update Scoring incase it wasn't updated previously
		buildScoringDetails(formatBuilder);

		const eventFormat = { ...formatBuilder.eventFormat };
		formatBuilder.data.stages = formatBuilder.data.stages.map(s => {
			switch (s.type) {
				case EventStageTypes.Swiss:
					return {
						...s,
						playoffDetails: null,
						groupStageDetails: null,
						bountyStageDetails: null,
						bracket: null
					};
				case EventStageTypes.Group:
					return {
						...s,
						playoffDetails: null,
						swissStageDetails: null,
						bountyStageDetails: null,
						bracket: s.groupStageDetails.hasGroupBrackets ? s.bracket : null
					};
				case EventStageTypes.Playoffs:
					return {
						...s,
						swissStageDetails: null,
						groupStageDetails: null,
						bountyStageDetails: null
					};
				case EventStageTypes.Bounty:
					return {
						...s,
						playoffDetails: null,
						swissStageDetails: null,
						groupStageDetails: null
					};
				default:
					console.error('You got a stage type that doesn\'t exist.');
					return { ...s };
			}
		});
		eventFormat.json = JSON.stringify(formatBuilder.data);

		if (!validateFormat(eventFormat, formatBuilder.data)) {
			return;
		}

		SetLoading(true, "Saving Format");
		try {
			const updatedFormat = await EventFormatService.saveFormat(eventFormat);
			const formatBuilder =
				EventFormatHelper.getEventFormatBuilder(updatedFormat);
			Store.set(StoreKeys.EVENTFORMATBUILDER.CURRENT_FORMAT, formatBuilder);
			Store.set(StoreKeys.EVENTFORMATBUILDER.EVENT_FORMATS, null);
		} catch (error) {
			console.error(error);
		}
		SetLoading(false);
	};

	const saveAndClose = async () => {
		await save();
		Store.set(StoreKeys.EVENTFORMATBUILDER.CURRENT_FORMAT, "");
	};

	const cancel = () => {
		Store.set(StoreKeys.EVENTFORMATBUILDER.CURRENT_FORMAT, "");
	};

	return (
		<div className={classNames.root}>
			{sidebarOpen ? (
				<aside className={classNames.sidebar}>
					<div className={classNames.sidebarHeader}>
						<h2 className={classNames.sidebarTitle}>Event Format Builder</h2>
						<IconButton
							size="small"
							aria-label="collapse sidebar"
							onClick={() => setSidebarOpen(false)}
						>
							<ChevronLeft color="primary" />
						</IconButton>
					</div>
					<div className={classNames.sidebarBody}>
						<EventFormatDetails />
					</div>
					<div className={classNames.sidebarFooter}>
						<Button color="primary" onClick={save}>
							SAVE
						</Button>
						<Button color="primary" onClick={saveAndClose}>
							SAVE AND CLOSE
						</Button>
						<Button color="secondary" onClick={cancel}>
							CANCEL
						</Button>
					</div>
				</aside>
			) : (
				<div className={classNames.collapseRail}>
					<IconButton
						size="small"
						aria-label="expand sidebar"
						onClick={() => setSidebarOpen(true)}
					>
						<ChevronRight color="primary" />
					</IconButton>
				</div>
			)}
			<main className={classNames.main}>
				<EventFormatBracketBuilder />
			</main>
		</div>
	);
}
